import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData } from "@/data";
import { getDistributionsListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDistributionsList = (releaseId, distributionAction, orderBy) => {
    const [params] = useSearchParams();

    const [cursor, setCursor] = useState(null);

    const dispatch = useDispatch();

    const pageParam = parseInt(params.get("page")) || 1;

    const limitParam = parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const { isAuthenticated } = useAuth0();

    const { t } = useTranslation();

    const { data, error, loading, previousData, startPolling, stopPolling } = useQuery(getDistributionsListQuery, {
        skip: !isAuthenticated,
        variables: {
            ...(cursor ? { cursor } : {}),
            limit,
            page: page - 1,
            ...(releaseId && { releaseId }),
            ...(distributionAction && { distributionAction }),
            ...(orderBy && { orderBy }),
        },
    });

    const distributionActionRef = useRef(distributionAction);

    useEffect(() => {
        if (distributionActionRef.current === distributionAction) return;

        distributionActionRef.current = distributionAction;

        setCursor(null);

        setPage(1);
    }, [distributionAction]);

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    // fall back to the previous list while new filter variables load, so the page and its filters stay mounted
    // and only the table rows show a loader
    const listData = data ?? previousData;

    return {
        count: listData?.listDistributions?.meta?.resultCount,
        data: listData?.listDistributions?.posts,
        isRefetching: loading && !data && !!previousData,
        limit,
        loading,
        nextCursor: listData?.listDistributions?.meta?.nextCursor,
        page: listData?.listDistributions?.meta?.currentPage,
        prevCursor: listData?.listDistributions?.meta?.prevCursor,
        setCursor,
        setLimit,
        setPage,
        startPolling,
        stopPolling,
    };
};

export default useDistributionsList;
